import {
  lazy,
  Suspense,
  useState,
  useEffect,
  useRef,
  useCallback,
} from 'react';
import { Outlet } from 'react-router-dom';

import { cn } from '@/lib/utils';
import { Header, HeaderProps, useHandleBackClick } from './Header';
import { Menu } from './Menu/Menu';
import { ReactComponent as ChevronL } from '../assets/ChevronL.svg';
import { Footer } from './Footer';
import { Debug } from './Debug';
import config from '../config';
import { useDebugMode } from '../context/appContext';
import { useShouldHideBackButton } from '../hooks/useShouldHideBackButton';
import { useDebugTheme } from './Debug/useDebugTheme';
import { GoChevronLeft } from 'react-icons/go';
import { GoChevronRight } from 'react-icons/go';
import { AnimatedOutlet } from './AnimatedOutlet';

// Dev/staging only: the fast-track picker never renders in production.
//
// This deliberately bypasses `config.env.production`. That value is a property
// on an object literal computed with `Boolean(...)`, and neither survives
// constant folding: the bundler keeps the branch, so the `lazy` import below
// is retained and the picker ships in the production bundle. Referencing
// `import.meta.env` directly, with `!!` rather than `Boolean(...)` (esbuild
// folds operators but not a `Boolean()` call, since the global could be
// shadowed), keeps this a build-time constant so the module is tree-shaken
// out entirely. Paired with the `define` for VITE_STAGING in vite.config.ts,
// without which an unset VITE_STAGING stays a runtime lookup and nothing folds.
const DevFastTrack =
  !import.meta.env.PROD || !!import.meta.env.VITE_STAGING
    ? lazy(() => import('./DevFastTrack'))
    : null;
interface MainLayoutProps {
  headerVariant?: HeaderProps['variant'];
  children?: React.ReactNode;
}

export const MainLayout = ({
  headerVariant = 'default',
  children = headerVariant === 'default' ? <AnimatedOutlet /> : <Outlet />,
}: MainLayoutProps) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [debugMode] = useDebugMode();
  useDebugTheme();

  // Apply gradient on <html> so it extends behind translucent system bars
  const isQuestionnaire = headerVariant === 'questionnaire';
  useEffect(() => {
    // background-image doesn't extend to iOS safe areas, only background-color does
    const midColor = isQuestionnaire ? '#FAFCFE' : '#F5F3F2';
    document.documentElement.style.backgroundColor = midColor;
    document.body.style.backgroundColor = 'transparent';

    const themeColor = isQuestionnaire ? '#F7FAFF' : '#E8F6FE';
    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta) meta.setAttribute('content', themeColor);

    return () => {
      document.documentElement.style.backgroundColor = '';
      document.body.style.backgroundColor = '';
      if (meta) meta.setAttribute('content', '#ffffff');
    };
  }, [isQuestionnaire]);
  // Defaults to open when unset, so only an explicit collapse is remembered.
  const [debugPanelOpen, setDebugPanelOpen] = useState(
    () => sessionStorage.getItem('debugPanelOpen') !== 'false',
  );
  const [debugPanelWidth, setDebugPanelWidth] = useState(() =>
    parseInt(sessionStorage.getItem('debugPanelWidth') || '520', 10),
  );
  const [isResizing, setIsResizing] = useState(false);
  const resizeStartX = useRef(0);
  const resizeStartWidth = useRef(0);
  const handleBackClick = useHandleBackClick();
  const [openWhyPrepareConsultation, setOpenWhyPrepareConsultation] =
    useState(false);
  const [openWhatIsAldebaran, setOpenWhatIsAldebaran] = useState(false);
  const shouldHideBackButton = useShouldHideBackButton();

  useEffect(() => {
    sessionStorage.setItem('debugPanelWidth', String(debugPanelWidth));
  }, [debugPanelWidth]);

  useEffect(() => {
    sessionStorage.setItem('debugPanelOpen', String(debugPanelOpen));
  }, [debugPanelOpen]);

  const handleResizeMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      setIsResizing(true);
      resizeStartX.current = e.clientX;
      resizeStartWidth.current = debugPanelWidth;
      document.body.style.userSelect = 'none';
    },
    [debugPanelWidth],
  );

  useEffect(() => {
    if (!isResizing) return;

    const handleMouseMove = (e: MouseEvent) => {
      const maxWidth = window.innerWidth * 0.5;
      const minWidth = 320;
      const delta = resizeStartX.current - e.clientX;
      const newWidth = Math.max(
        minWidth,
        Math.min(maxWidth, resizeStartWidth.current + delta),
      );
      setDebugPanelWidth(newWidth);
    };

    const handleMouseUp = () => {
      setIsResizing(false);
      document.body.style.userSelect = '';
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing]);

  const onOpenMenu = useCallback(() => {
    console.log('Opening menu');
    setMenuOpen(true);
  }, [setMenuOpen]);
  const onCloseMenu = useCallback(() => {
    console.log('Closing menu');
    setOpenWhyPrepareConsultation(false);
    setOpenWhatIsAldebaran(false);
    setMenuOpen(false);
  }, [setMenuOpen]);

  return (
    <>
      {/* Overlay when menu is open */}
      {menuOpen && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 transition-all duration-300 ease-in-out" />
      )}

      <div
        className={cn(
          'fixed inset-0 flex flex-col items-center justify-between',
          isQuestionnaire
            ? 'bg-gradient-to-tr from-[#FDFDFC] to-[#F7FAFF]'
            : 'bg-gradient-to-tr from-[#FCF1ED] to-[#E8F6FE]',
        )}
        style={{
          paddingTop: 'env(safe-area-inset-top)',
          paddingBottom: 'env(safe-area-inset-bottom)',
          ...(config.debug.panel && debugMode && debugPanelOpen
            ? { marginRight: debugPanelWidth }
            : {}),
        }}
      >
        {/* Desktop Header */}
        <div className="hidden nsm:flex w-full">
          <Header
            variant={headerVariant}
            onOpenMenu={onOpenMenu}
            display="desktop"
          />
          <Menu
            open={menuOpen}
            onClose={onCloseMenu}
            openWhyPrepareConsultation={openWhyPrepareConsultation}
            openWhatIsAldebaran={openWhatIsAldebaran}
          />
        </div>

        <div
          className={cn(
            'relative w-full max-w-full h-full flex flex-col overscroll-contain no-scrollbar rounded-none nsm:rounded-2xl',
            'nsm:overflow-y-auto',
            headerVariant === 'questionnaire' &&
              'nxl:w-193 nmd:w-148 nmd:h-full',
          )}
        >
          <div className="nsm:hidden shrink-0">
            <Header
              variant={headerVariant}
              onOpenMenu={onOpenMenu}
              display="mobile"
            />
          </div>

          {/*
            Les écrans d'onboarding tiennent normalement dans la fenêtre : pas
            de défilement, l'app se comporte comme une application native. Mais
            `overflow-hidden` transformait tout dépassement en contenu
            INJOIGNABLE – le conteneur refuse le défilement et la coque est en
            `fixed inset-0`, donc la page non plus. Un nom de cabinet un peu
            long, une police système agrandie, un zoom navigateur ou un petit
            téléphone suffisaient à rendre le CTA principal inatteignable, sans
            le moindre signe visible (ALDO-2747).

            `overflow-y-auto` ne change rien tant que la page tient : aucune
            barre n'apparaît, et `no-scrollbar` les masque de toute façon. Il
            supprime seulement le cas où le contenu débordant disparaît.
            `overflow-x-hidden` reste, donc pas de défilement horizontal.
          */}
          <main
            className={cn(
              'w-full min-h-0 flex-1 overflow-x-hidden overscroll-contain relative no-scrollbar pt-0 nsm:pt-4',
              headerVariant === 'questionnaire'
                ? 'overflow-y-auto nsm:px-10'
                : 'mx-auto max-w-154 nsm:px-10 overflow-y-auto',
            )}
          >
            {/* Back button for Desktop */}
            <button
              className={`hidden nsm:flex absolute z-20 py-2 px-3 hover:bg-gray-50 rounded-lg cursor-pointer ${
                headerVariant === 'questionnaire'
                  ? 'top-11 -ml-6'
                  : 'top-8 left-0'
              } ${shouldHideBackButton ? '!hidden' : ''}`}
              onClick={handleBackClick}
            >
              <ChevronL />
            </button>
            {children}
          </main>
        </div>

        {headerVariant === 'questionnaire' && (
          <Footer
            onWhyPrepareConsultationClick={() => {
              setMenuOpen(true);
              setOpenWhyPrepareConsultation(true);
            }}
            onWhatIsAldebaranClick={() => {
              setMenuOpen(true);
              setOpenWhatIsAldebaran(true);
            }}
          />
        )}
      </div>

      {/* Gated on `debugMode` as well as the build-time check above, so the
          picker only appears once debug mode is switched on. Keeping the gate
          here rather than inside the component also avoids fetching the lazy
          chunk at all while debug mode is off.

          Suspense is required: this app has no other Suspense boundary, and a
          lazy component without one throws straight into the ErrorBoundary. */}
      {DevFastTrack && debugMode && (
        <Suspense fallback={null}>
          <DevFastTrack />
        </Suspense>
      )}

      {config.debug.panel && debugMode && (
        <>
          {/* Debug Panel */}
          <div
            className="absolute top-0 right-0 h-full bg-[var(--dbg-bg)] border-l border-[var(--dbg-border)] overflow-y-auto overscroll-contain z-50 transition-[width] duration-300 ease-in-out"
            style={{ width: debugPanelOpen ? debugPanelWidth : 0 }}
          >
            {/* Drag Handle */}
            {debugPanelOpen && (
              <div
                className="absolute top-0 left-0 bottom-0 w-1 cursor-col-resize bg-transparent hover:bg-blue-500 transition-colors"
                style={{ zIndex: 51 }}
                onMouseDown={handleResizeMouseDown}
              />
            )}
            <div className={`${debugPanelOpen ? 'block' : 'hidden'}`}>
              <Debug />
            </div>
          </div>

          {/* Toggle Bar */}
          <div
            className="absolute top-1/2 right-0 h-16 bg-[var(--dbg-bg-secondary)] border border-[var(--dbg-border)] rounded-l-lg shadow-lg cursor-pointer z-50 transition-transform duration-300 ease-in-out flex items-center"
            style={{
              transform: debugPanelOpen
                ? `translateX(-${debugPanelWidth}px)`
                : 'translateX(0)',
            }}
            onClick={() => setDebugPanelOpen(!debugPanelOpen)}
          >
            <div className="flex items-center justify-center w-6 h-full bg-[var(--dbg-hover)] rounded-l-lg">
              <span className="text-[var(--dbg-text-muted)] text-sm">
                {!debugPanelOpen ? <GoChevronLeft /> : <GoChevronRight />}
              </span>
            </div>
          </div>
        </>
      )}
    </>
  );
};
